import { useMemo, type FC } from 'react';
import { useCustom } from '@refinedev/core';
import dayjs, { type Dayjs } from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Spin } from 'antd';
import CalendarEntry, { type CalendarEntryData } from './CalendarEntry';

dayjs.extend(weekday);
dayjs.extend(isoWeek);

const DAYS_HEADER = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

interface DayCell {
  date: Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  entries: CalendarEntryData[];
}

interface ApiEntry {
  id: string;
  title: string;
  type: string;
  persona: string | null;
  scheduledDate: string;
  status: string;
  hasContent: boolean;
  generatedFrom: string | null;
  createdAt: string;
}

interface CalendarMonthResponse {
  year: number;
  month: number;
  entries: ApiEntry[];
  totalEntries: number;
}

interface CalendarGridProps {
  month: Dayjs;
  onEntryClick: (entry: CalendarEntryData) => void;
  onDayClick: (date: Dayjs) => void;
  viewMode: 'month' | 'week';
}

const CalendarGrid: FC<CalendarGridProps> = ({ month, onEntryClick, onDayClick, viewMode }) => {
  const { data, isLoading } = useCustom<CalendarMonthResponse>({
    url: '/api/v1/admin/marketing/calendar',
    method: 'get',
    config: { query: { year: month.year(), month: month.month() + 1 } },
    queryOptions: { queryKey: ['calendar-month', month.year(), month.month()] },
  });

  const entries: CalendarEntryData[] = useMemo(() => {
    if (!data?.data?.entries) return [];
    return data.data.entries.map((e) => ({
      id: e.id,
      title: e.title,
      type: e.type as CalendarEntryData['type'],
      status: e.status as CalendarEntryData['status'],
      scheduledDate: e.scheduledDate,
      persona: e.persona ?? undefined,
    }));
  }, [data]);

  const weeks = useMemo(() => {
    const entriesByDate = new Map<string, CalendarEntryData[]>();
    entries.forEach((e) => {
      const key = dayjs(e.scheduledDate).format('YYYY-MM-DD');
      if (!entriesByDate.has(key)) entriesByDate.set(key, []);
      entriesByDate.get(key)!.push(e);
    });

    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const startOfGrid = startOfMonth.startOf('isoWeek');
    const endOfGrid = endOfMonth.endOf('isoWeek');

    const result: DayCell[][] = [];
    let current = startOfGrid;
    const today = dayjs().format('YYYY-MM-DD');

    while (current.isBefore(endOfGrid) || current.isSame(endOfGrid, 'day')) {
      const week: DayCell[] = [];
      for (let i = 0; i < 7; i++) {
        const dateStr = current.format('YYYY-MM-DD');
        week.push({
          date: current,
          isCurrentMonth: current.month() === month.month(),
          isToday: dateStr === today,
          entries: entriesByDate.get(dateStr) ?? [],
        });
        current = current.add(1, 'day');
      }
      result.push(week);
    }

    return result;
  }, [month, entries]);

  const displayedWeeks = useMemo(() => {
    if (viewMode === 'month') return weeks;
    const today = dayjs();
    const targetWeek = weeks.find((w) => w.some((d) => d.date.isSame(today, 'day')));
    return targetWeek ? [targetWeek] : weeks.length > 0 ? [weeks[0]] : [];
  }, [weeks, viewMode]);

  const cellHeight = viewMode === 'week' ? 'min-h-[200px]' : 'min-h-[100px]';

  if (isLoading) {
    return (
      <div className="fc-card flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fc-card overflow-hidden">
      {/* En-tête jours */}
      <div className="grid grid-cols-7 gap-px bg-slate-200 dark:bg-slate-700">
        {DAYS_HEADER.map((day) => (
          <div
            key={day}
            className="bg-slate-50 py-2 text-center text-xs font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Cases */}
      <div className="bg-slate-200 dark:bg-slate-700">
        {displayedWeeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-px">
            {week.map((day) => (
              <div
                key={day.date.format('YYYY-MM-DD')}
                onClick={() => onDayClick(day.date)}
                className={`${cellHeight} cursor-pointer bg-white p-1.5 transition-colors hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700/60 ${
                  !day.isCurrentMonth ? 'opacity-40' : ''
                }`}
              >
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium ${
                    day.isToday ? 'bg-blue-600 text-white' : 'text-slate-600 dark:text-slate-400'
                  }`}
                >
                  {day.date.format('D')}
                </span>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                <div className="mt-1 space-y-0.5" onClick={(e) => e.stopPropagation()}>
                  {day.entries.map((entry) => (
                    <CalendarEntry key={entry.id} entry={entry} onClick={onEntryClick} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarGrid;
